/**
 * GLOBAL TOOLBAR v3 (mockups/toolbar-v3.html + mockups/TOOLBAR-SPEC.md —
 * a deviation from the mockup is a bug). Two-row, sticky, on every page.
 *
 * Design language "Soft Light": the mockup's CSS is ported verbatim, scoped
 * under `.tbv3` (toolbarStyles), with dark overrides for the app's `.dark`
 * toggle. Governing rule — CALM BY DEFAULT: healthy counts render grey; amber
 * is spent only on a genuine exception (stuck stage, unread, needs-you).
 *
 * Placeholder emoji in the mockup are replaced with the app's lucide icons.
 * Live data is wired to existing endpoints. The Inbox slide-over surfaces new
 * email from the Mailroom (GET /mailbox/inbox-peek) alongside the tasks that
 * need your hand; Texts and toolbar compose stay stubbed behind a typed
 * interface with a TODO and route to the owning page, never faked inline.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCloseoutOnYou } from "@/components/closeout/whose-turn";
import { isMailroomPath, useMailLiveRefresh } from "@/lib/mail-live";
import {
    Search,
    Radio,
    Sun,
    Moon,
    Target,
    LayoutGrid,
    CheckCircle2,
    ListChecks,
    Landmark,
    ChevronDown,
    Plus,
    PiggyBank,
    Send,
    ClipboardList,
    X,
    AlertTriangle,
    Users,
    DollarSign,
    Inbox,
    Mail,
    Camera,
    ShieldCheck,
    BarChart3,
    Package,
    ShoppingCart,
    Boxes,
    TrendingUp,
    Star,
    Settings,
    Briefcase,
    Building2,
    MapPin,
    Receipt,
    FileText,
    HardDrive,
    Paperclip,
    UserCircle,
    Globe,
    GraduationCap,
    History,
    Phone,
    ClipboardCheck,
    UserX,
    TreePalm,
} from "lucide-react";
import { api, downloadAuthedFile } from "@/lib/api";
import { BudgetAnswerCard } from "@/pages/customers/budget/ask-card";
import { shortCustomerName, type BudgetAnswer } from "@/pages/customers/budget/model";
import { useWorkCounts } from "@/lib/counts";
import { useFriendlyMailroom } from "@/lib/friendly-mailroom";
import { InboxChip } from "./inbox-chip";
import { TasksChip } from "./tasks-chip";
import { CheckChip } from "./check-chip";
import { openTaskHere } from "@/lib/task-flyout";
import { maskMoney } from "@/lib/mask-money";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { useNewRequestsSwitch } from "@/lib/new-requests";
import { BookingChip, bookingChipCss } from "./booking-chip";
import { RequestsChip } from "./requests-chip";
import { CloseoutHoldChip } from "@/components/card-hold/closeout-hold-chip";
import { FieldChip, fieldChipCss } from "@/components/field-add/field-chip";
import { QuotesWaitingChip, quotesWaitingChipCss } from "./quotes-waiting-chip";
import { DeadlinesChip, deadlinesChipCss } from "./deadlines-chip";
import { MismatchChip } from "@/components/mismatch/mismatch-chip";
import { TeachIntro } from "@/teach/teach-card";
import { LOGO_SRC } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { useTeach } from "@/teach/teach-state";
import { PERMISSIONS } from "@/lib/permissions";
import { useQuickBook } from "@/lib/quick-book-context";
import { SupportCopilotThread, useSupportCopilotChat } from "@/components/support-copilot";
import { JobCheckupPanel } from "@/components/job-checkup-panel";
import { MyQuestions } from "@/components/help-desk-my-questions";
import { HelpDeskReview, useHelpDeskReview } from "@/components/help-desk-review";
import { useMyCases } from "@/lib/use-my-cases";
import { HELP_DESK_CLOSE_EVENT, HELP_DESK_OPEN_EVENT } from "@/lib/use-job-checkup";
import { capturePageContext } from "@/lib/page-context";
import { openPriceCheck } from "@/components/price-check";
import { TimeClockPunch } from "@/components/time-clock-punch";
import { NotificationBell } from "@/components/notification-bell";
import { useChatToasts } from "@/lib/use-chat-toasts";
import { type InboxResponse } from "@/lib/tasks";
import { type MailPeek, type UnifiedRow, mailToRow, taskToRow, sortUnified } from "@/lib/inbox";
import { MAIL_LATE_WORKING_HOURS, type MailCount } from "@/lib/mail-count";
import { toolbarCss } from "./toolbarStyles";
import { OPEN_PALETTE_EVENT, useJobActionsPublication } from "@/lib/job-actions-bus";
import { matchesJobAction, type JobAction } from "@/pages/jobs/detail/jobActions";
import { toast } from "sonner";
import { WhatsNewDialog, useWhatsNewAvailable } from "@/teach/whats-new";

// ─── Types (stub where no endpoint exists yet) ──────────────────────────
interface WorkPulse {
    new: number;
    scheduled: number;
    field: number;
    review: number;
    risk: number;
}
interface SearchResult {
    type: string;
    id: string;
    title: string;
    subtitle?: string;
    parentId?: string;
}
// Task rows (GET /api/tasks/my-inbox) and the Mailroom email peek
// (GET /api/mailbox/inbox-peek) are typed in @/lib/tasks and @/lib/inbox — one
// shape shared with the other inbox surfaces. Each carries a real `href`, so a
// click always lands on the thing (the old local TaskItem had a `links` array the
// endpoint never sends, which is why every task row fell back to /tasks).

// Stable avatar tint per sender, drawn from the toolbar's four ink colors so the
// email rows sit in the same palette as the rest of the chrome.
const MAIL_AVATARS = ["var(--i-office)", "var(--i-corbin)", "var(--i-billing)", "var(--i-filings)"];
function mailAvatar(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return MAIL_AVATARS[Math.abs(h) % MAIL_AVATARS.length];
}
function mailInitial(from: string): string {
    const c = from
        .trim()
        .replace(/^[<"']+/, "")
        .charAt(0);
    return c ? c.toUpperCase() : "@";
}
/** Today → time; otherwise short date. Blank on bad/absent input. */
function peekWhen(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toDateString() === new Date().toDateString()
        ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// Nav lineup (spec Row 2). Mailroom is the shared-inbox over Gmail (Native
// Mailbox) — unhidden 2026-07-20 now that the room is real (was off the menu
// while it was a stub).
const NAV: Array<{
    label: string;
    to: string;
    icon: typeof Target;
    badge?: "tasks" | "purchasing";
    perm?: string;
    /** Hover explainer — the one-liner the More menu shows, here as a title. */
    tip?: string;
}> = [
    { label: "Command Center", to: "/", icon: Target, tip: "What needs attention today" },
    { label: "The Grid", to: "/dispatch", icon: LayoutGrid, tip: "Schedule & dispatch — who goes where, when" },
    { label: "Closeout", to: "/close-out", icon: CheckCircle2, tip: "Review and approve completed jobs" },
    { label: "Mailroom", to: "/mailroom", icon: Mail, tip: "The company email boxes" },
    { label: "Tasks", to: "/tasks", icon: ListChecks, badge: "tasks", tip: "Everything waiting on a person" },
    { label: "City Filing", to: "/submissions", icon: Landmark, tip: "Send finished test reports to each city" },
    // Purchasing was reachable only via ⌘K + the dashboard blocking card;
    // surfaced here (office/inventory only) with a live "needs a person" badge.
    {
        label: "Purchasing",
        to: "/purchasing",
        icon: ShoppingCart,
        badge: "purchasing",
        perm: PERMISSIONS.inventory.manage,
    },
];

// "More ▾" opens the launcher (mockups/gwc-more-section.html): destinations
// grouped by what you're doing — Start here / Queues / Run the business — plus
// an admin-only Admin group and a "moved, not lost" map and a door into the
// 7-room Settings console. A deviation from the mockup is a bug — EXCEPT the
// Admin group, added post-mockup per Issue #200 to give the form-template admin
// tools (Form Templates, Template Health) a launcher home instead of being
// reachable only by search or a direct URL.
type LaunchTile = {
    label: string;
    to: string;
    sub: string;
    icon: typeof Target;
    perm?: string;
    adminOnly?: boolean;
    badge?: "review"; // wired to live pulse; illustrative counts are not faked
};

const LAUNCH_START: LaunchTile[] = [
    { label: "Command Center", to: "/", sub: "What needs attention", icon: Target },
    { label: "The Grid", to: "/dispatch", sub: "Schedule & dispatch", icon: LayoutGrid },
    { label: "Customers", to: "/customers", sub: "Accounts, sites, devices", icon: Users },
    { label: "Jobs", to: "/jobs", sub: "All work orders", icon: Briefcase },
    { label: "Money", to: "/money", sub: "Invoices & payments", icon: DollarSign, perm: PERMISSIONS.finance.view },
];
const LAUNCH_QUEUE: LaunchTile[] = [
    { label: "Intake Desk", to: "/jobs/work-orders", sub: "New requests", icon: Inbox },
    { label: "Online Booking", to: "/booking", sub: "Website requests → leads", icon: Globe },
    { label: "Closeout", to: "/close-out", sub: "Approve completed", icon: CheckCircle2, badge: "review" },
    { label: "City Filing", to: "/submissions", sub: "File to cities", icon: Landmark },
    {
        label: "Purchasing",
        to: "/purchasing",
        sub: "Order parts · track POs",
        icon: ShoppingCart,
        perm: PERMISSIONS.inventory.manage,
    },
    // #831 M2+M13: the two parts screens were reachable from no menu at all
    // (found only by reading the route table), and staging is a daily 4 PM
    // task — so they live here in the daily band, not under "weekly".
    { label: "Parts Custody", to: "/inventory/parts-custody", sub: "Who holds each part now", icon: Boxes },
    { label: "Parts to Stage", to: "/reports/parts-to-stage", sub: "Stage tomorrow's parts · 4 PM", icon: Package },
    { label: "Photo Backlog", to: "/jobs/photo-backlog", sub: "Unfiled photos", icon: Camera },
    { label: "My Uploads", to: "/my-uploads", sub: "Files you dropped in", icon: Paperclip },
    // #1426: the door to the whole Compliance section (licenses & insurance,
    // city filing, water providers and the other set-up screens).
    { label: "Compliance", to: "/compliance", sub: "Licenses, city filing, and who we file with", icon: ShieldCheck },
];
// #1324: the "Inventory" tile (→ /inventory) was removed from this band. The
// warehouse module holds ZERO rows from actual work — its two setup rows date
// from 2026-04-16 — and no pricebook item links to it, so the qty-on-hand
// column on every PO line already reads blank. Parts Custody, the live system,
// keeps its tile in LAUNCH_DAILY above. The routes still resolve for a
// bookmark; they are only hidden. Do not re-add this without re-running
// `node dist/server/scripts/inventoryUsageReport.js` first.
const LAUNCH_RUN: LaunchTile[] = [
    { label: "Insights", to: "/analytics", sub: "Analytics · Job Profit · Reports", icon: BarChart3 },
    {
        label: "Payroll",
        to: "/payroll",
        sub: "Run payroll · Job Review · points",
        icon: DollarSign,
        perm: PERMISSIONS.payroll.view,
    },
    {
        label: "Employee Report",
        to: "/admin/employee-report",
        sub: "One employee · honest activity",
        icon: UserCircle,
        adminOnly: true,
    },
    {
        label: "Fieldbook",
        to: "/pricebook",
        sub: "Prices, costs & bundles",
        icon: Boxes,
        perm: PERMISSIONS.pricing.view,
    },
    { label: "Sales Pipeline", to: "/pipeline", sub: "Open estimates & leads", icon: TrendingUp },
    { label: "Reviews", to: "/reviews", sub: "Reputation", icon: Star },
];
// People (#2153) — who's in, who's out. HR Cockpit had no menu entry at all and
// Attendance / Time Off were only inside Settings → Company & People (they stay
// there too; this is a front door). Each tile needs the permission its page's
// data needs, so a group with nothing you can open is not shown.
export const LAUNCH_PEOPLE: LaunchTile[] = [
    {
        label: "HR Cockpit",
        to: "/people/hr-cockpit",
        sub: "Call-ins first, then patterns",
        icon: ClipboardCheck,
        perm: PERMISSIONS.hr.view,
    },
    {
        label: "Attendance",
        to: "/people/attendance",
        sub: "Who missed what, and points",
        icon: UserX,
        perm: PERMISSIONS.hr.view,
    },
    {
        label: "Time Off",
        to: "/people/pto-requests",
        sub: "Ask for and approve days off",
        icon: TreePalm,
        perm: PERMISSIONS.users.view,
    },
];
// Admin / setup (Issue #200) — power-user configuration that had no launcher
// home, so it was reachable only by direct URL or by digging into the Settings
// console (Form Templates was buried under Compliance → Form Mapper; Template
// Health had no nav entry at all). Every tile is adminOnly, so the whole group
// stays hidden for non-admins.
const LAUNCH_ADMIN: LaunchTile[] = [
    {
        label: "Form Templates",
        to: "/compliance/forms",
        sub: "Form PDFs & field mapping",
        icon: FileText,
        adminOnly: true,
    },
    {
        label: "Template Health",
        to: "/admin/form-templates/coverage",
        sub: "Coverage & gaps by workflow",
        icon: ClipboardList,
        adminOnly: true,
    },
    { label: "Job Types", to: "/admin/job-types", sub: "Services & default crew", icon: ListChecks, adminOnly: true },
    {
        label: "System Health",
        to: "/admin/system-health",
        sub: "Workers, jobs & status",
        icon: HardDrive,
        adminOnly: true,
    },
    {
        label: "Voice Training",
        to: "/training",
        sub: "Practice calls vs an AI customer",
        icon: GraduationCap,
        adminOnly: true,
    },
];

// "Moved, not lost" — pages that stopped being their own menu item; the routes
// stay live, they just leave the menu (you reach them faster now).
const MOVED: Array<[string, string]> = [
    ["Device Lookup", "search"],
    ["All Devices", "site record"],
    ["All Test Reports", "the device"],
    ["Call Logs", "Comms tab"],
    ["Text Messages", "Comms tab"],
    ["Locations", "inside Customers"],
    ["Certificates of Insurance", "the record"],
    ["Warranties", "the device"],
    ["Job Profit", "Insights"],
    ["Analytics", "Insights"],
    ["Barcode Scan", "field app"],
    ["My Jobs / My Points", "field view"],
];

/**
 * New requests (10/28, pick 1A): with "Try New requests" on, ONE home for new
 * customers. The Online Booking tile opens New requests (widgets stay on
 * /booking, one tap away from there), the Sales Pipeline tile goes (its board
 * redirects to New requests), and the Intake Desk stops saying "New requests"
 * so the word means one thing.
 */
export function launchQueueFor(tiles: LaunchTile[], newRequestsOn: boolean): LaunchTile[] {
    if (!newRequestsOn) return tiles;
    return tiles.map((t) =>
        t.to === "/booking"
            ? { ...t, label: "New requests", to: "/requests", sub: "Call new customers back", icon: Phone }
            : t.to === "/jobs/work-orders"
              ? { ...t, sub: "Work orders in" }
              : t,
    );
}
export function launchRunFor(tiles: LaunchTile[], newRequestsOn: boolean): LaunchTile[] {
    return newRequestsOn ? tiles.filter((t) => t.to !== "/pipeline") : tiles;
}

// Flat list feeds the ⌘K "Jump to" section so every launcher destination (plus
// the Settings console) stays searchable.
const MORE: Array<{ label: string; to: string; perm?: string; adminOnly?: boolean }> = [
    ...LAUNCH_START,
    ...LAUNCH_QUEUE,
    ...LAUNCH_RUN,
    ...LAUNCH_PEOPLE,
    ...LAUNCH_ADMIN,
    { label: "Settings", to: "/settings", sub: "Company, compliance, pricing & more", icon: Settings },
].map((t) => ({ label: t.label, to: t.to, perm: t.perm, adminOnly: t.adminOnly }));

function useDarkToggle(): [boolean, () => void] {
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    const toggle = useCallback(() => {
        const next = !document.documentElement.classList.contains("dark");
        document.documentElement.classList.toggle("dark", next);
        try {
            localStorage.setItem("theme", next ? "dark" : "light");
        } catch {
            /* ignore */
        }
        setDark(next);
    }, []);
    return [dark, toggle];
}

function resultPath(r: SearchResult): string {
    switch (r.type) {
        case "customer":
            return `/customers/${r.id}`;
        case "site":
        case "location":
            return `/sites/${r.id}`;
        case "asset":
        case "device":
            return `/assets/${r.id}`;
        case "invoice":
            return `/money?tab=invoices&focus=${r.id}`;
        case "estimate":
            return `/commercial/${r.id}?type=estimate`;
        case "job":
            return `/jobs/${r.id}`;
        case "attachment":
            return r.parentId ? `/jobs/${r.parentId}#attachments` : `/attachments?q=${encodeURIComponent(r.title)}`;
        // A work order still in intake — its own review screen, NOT /jobs/<id>:
        // the id is an intake item, not a job (#984).
        case "work_order":
            return `/jobs/work-orders/review/${r.id}`;
        case "user":
            return `/admin/users/${r.id}`;
        default:
            return `/jobs/${r.id}`;
    }
}

// Per-type display: icon + label + pill colors, so every Find row plainly reads
// as Customer / Location / Job / Invoice / Estimate / Device / File / User. The
// pill is a light tint with a saturated label — legible on both the light and
// dark palette grounds.
const FIND_META: Record<string, { label: string; icon: typeof Search; color: string; bg: string }> = {
    customer: { label: "Customer", icon: Building2, color: "#2563eb", bg: "#DCE8FE" },
    site: { label: "Location", icon: MapPin, color: "#0891b2", bg: "#CFF2F7" },
    location: { label: "Location", icon: MapPin, color: "#0891b2", bg: "#CFF2F7" },
    job: { label: "Job", icon: Briefcase, color: "#d97706", bg: "#FCEBCF" },
    invoice: { label: "Invoice", icon: Receipt, color: "#059669", bg: "#CFF3E4" },
    estimate: { label: "Estimate", icon: FileText, color: "#7c3aed", bg: "#ECE3FB" },
    asset: { label: "Device", icon: HardDrive, color: "#9333ea", bg: "#EEE0FB" },
    device: { label: "Device", icon: HardDrive, color: "#9333ea", bg: "#EEE0FB" },
    attachment: { label: "File", icon: Paperclip, color: "#ea580c", bg: "#FBE3D2" },
    work_order: { label: "Work order", icon: ClipboardList, color: "#0d9488", bg: "#CCF0EA" },
    user: { label: "User", icon: UserCircle, color: "#e11d48", bg: "#FBD9E2" },
};
function findMeta(type: string) {
    return FIND_META[type] ?? { label: type, icon: Search, color: "var(--ink-soft)", bg: "#F1ECE2" };
}

/**
 * A11Y (#1205) — a closed slide-over is `aria-hidden`, but it stays in the DOM
 * with its buttons still focusable. That combination is the worst of both
 * worlds: the keyboard lands there and the screen reader has been told the
 * subtree does not exist, so it announces nothing. Measured on 2026-08-25:
 * tabbing off "Needs you" fell through THIRTY off-screen controls before
 * reaching the page.
 *
 * `inert` removes the subtree from the tab order AND the accessibility tree in
 * one attribute, with no visual effect. React 18 has no typed `inert` prop, so
 * it is spread in as an attribute; React 19 will accept it as a boolean.
 */
function closedOverlay(open: boolean): Record<string, string> {
    return open ? {} : { inert: "" };
}

export function TopBarV3() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, can, logout, isOffice } = useAuth();
    // #1465 step 3 — mail on the live pipe (lib/mail-live.ts). Office only,
    // like the badge. On the Mailroom itself the page holds the subscription,
    // so one mail event never triggers two rounds of refetches.
    useMailLiveRefresh(isOffice && !isMailroomPath(location.pathname));
    const quickBook = useQuickBook();
    const canSeeMoney = can(PERMISSIONS.financials.view) || can(PERMISSIONS.finance.view);
    const isAdmin = useAuth().isAdmin;

    const [dark, toggleDark] = useDarkToggle();
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [query, setQuery] = useState("");
    // Debounced form of `query` — the search request keys off this so we fire
    // one request after typing settles, not one per keystroke.
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [sel, setSel] = useState(0);
    const [moreOpen, setMoreOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const [panel, setPanel] = useState<null | "inbox" | "help">(null);
    // #1669 — what the person typed into the Help Desk. It used to be thrown
    // away on Enter; now it rides into the copilot's draft.
    const [helpText, setHelpText] = useState("");
    // #1681 — "Ask" (this screen) or "My questions" (what you've reported).
    const [helpTab, setHelpTab] = useState<"ask" | "mine" | "review">("ask");
    // #1947 Phase 3: the Admins' Review list. Its number is the only signal
    // (no bell, no push).
    const review = useHelpDeskReview(!!isAdmin);
    const reviewN = (review.data?.waiting?.length ?? 0) + (review.data?.drafts?.length ?? 0);
    const canFlag = can(PERMISSIONS.support.flag);
    const myCases = useMyCases(canFlag && panel === "help");
    const toCheck = (myCases.data?.cases ?? []).filter((c) => c.group === "check").length;
    // #1669 — on a job, the Help Desk opens on that job's check-up. Office
    // roles only (the same permission that sends the packet a fix can release).
    const canCheckup = can(PERMISSIONS.officeReview.manage);
    const helpJobId = useMemo(() => {
        if (!canCheckup) return null;
        const ctx = capturePageContext(location);
        return ctx.entity?.type === "jobs" ? ctx.entity.id : null;
    }, [canCheckup, location]);
    // #1703 — ONE Help Desk: the conversation lives in this panel (it used to
    // close the panel and open a second "Help" drawer that asked again). Held
    // here, in the always-mounted top bar, so it survives close/reopen.
    const chat = useSupportCopilotChat();
    const chatRef = useRef(chat);
    chatRef.current = chat;
    useEffect(() => {
        const onOpen = (e: Event) => {
            setPanel("help");
            setHelpTab("ask");
            const text = (e as CustomEvent<{ text?: string } | undefined>).detail?.text;
            if (text) chatRef.current.start(text);
        };
        const onClose = () => setPanel((p) => (p === "help" ? null : p));
        window.addEventListener(HELP_DESK_OPEN_EVENT, onOpen);
        window.addEventListener(HELP_DESK_CLOSE_EVENT, onClose);
        return () => {
            window.removeEventListener(HELP_DESK_OPEN_EVENT, onOpen);
            window.removeEventListener(HELP_DESK_CLOSE_EVENT, onClose);
        };
    }, []);
    const askHelpDesk = (text?: string) => {
        const t = (text ?? helpText).trim();
        setHelpText("");
        // The question is ASKED, not parked in a draft (2026-10-01 test drive).
        chat.start(t || undefined);
    };
    // Inbox slide-over filter: "On you" (default — the clearable, personal set) or
    // "All" (adds already-seen backlog still in the peek).
    const [inboxTab, setInboxTab] = useState<"you" | "all">("you");
    // The per-person "Try the new Mailroom" switch also turns on the friendly
    // Inbox chip (mailroom slice 1, 10/9) — one switch, one experience.
    // The office's switch (10/28) can turn it on for someone, too.
    const [friendlyMail] = useFriendlyMailroom({ enabled: isOffice });
    // Closeout "Whose turn" (10/28): the Closeout number is what's on you now.
    const closeoutOnYou = useCloseoutOnYou(isOffice);
    // The Quiet Toolbar (10/10, lib/quiet-toolbar.ts): per-person switch. On:
    // three chips that each count only what they own (Inbox, Tasks, Stuck),
    // and the repeat counters below (Tasks tab badge, "N back", Needs you)
    // are gone — the ONE Tasks chip carries on-you + came-back.
    const [quiet, setQuiet] = useQuietToolbar();
    const newRequests = useNewRequestsSwitch();
    // No bell on the quiet toolbar, but a live chat message still toasts.
    useChatToasts(isOffice && quiet);
    const searchRef = useRef<HTMLInputElement>(null);
    const cmdRef = useRef<HTMLDivElement>(null);

    // ── Live data ────────────────────────────────────────────────────────
    const pulse = useQuery({
        queryKey: ["work-pulse"],
        queryFn: () => api.get<WorkPulse>("/api/stats/work-pulse"),
        refetchInterval: 30_000,
    }).data;
    const myTasks = useQuery({
        queryKey: ["tasks", "my-inbox"],
        queryFn: () => api.get<InboxResponse>("/api/tasks/my-inbox"),
        refetchInterval: 60_000,
    }).data;
    // New email for the Inbox slide-over. Office-only (the shared box is an
    // office surface; field techs never see it), so field devices don't poll.
    const mailPeek = useQuery({
        queryKey: ["mailbox", "inbox-peek"],
        queryFn: () => api.get<MailPeek>("/api/mailbox/inbox-peek"),
        refetchInterval: 60_000,
        enabled: isOffice,
    }).data;
    // ONE honest mail number (Mailroom v2 PR 3, GET /api/mailbox/count): the
    // emails that need THIS person — unassigned + assigned to them, for the
    // Mailroom team only; FYI never counts; one conversation in several boxes
    // counts once. Replaces the old "N new to you" + "N aged past working
    // hours" pair, which read 1,954 and 1,944 for a queue of about 45.
    const mailCount = useQuery({
        queryKey: ["mailbox", "count"],
        queryFn: () => api.get<MailCount>("/api/mailbox/count"),
        refetchInterval: 60_000,
        enabled: isOffice,
    }).data;
    // Amber only when what needs you has waited past a few working hours.
    const breachingN =
        mailCount && (mailCount.oldestWaitingHours ?? 0) >= MAIL_LATE_WORKING_HOURS ? mailCount.needYou : 0;
    // Purchasing "needs a person" count → the Purchasing nav badge. Only fetched
    // for users who can see purchasing (same gate as the route + nav item).
    const purchasingN =
        useQuery({
            queryKey: ["purchasing-attention-nav"],
            queryFn: () => api.get<{ needs_attention: number }>("/api/purchasing/attention"),
            refetchInterval: 60_000,
            // Quiet toolbar: no Purchasing badge (most of its items are already
            // generated tasks — po_quote, rfq_stale, po_receiving, po_delivery —
            // so the Tasks chip counts them once), so don't fetch it.
            enabled: can(PERMISSIONS.inventory.manage) && !quiet,
        }).data?.needs_attention ?? 0;
    // Debounce the query (250 ms) before it drives the search request.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
        return () => clearTimeout(t);
    }, [query]);
    const searchResults = useQuery({
        queryKey: ["toolbar-search", debouncedQuery],
        queryFn: () => api.get<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(debouncedQuery)}`),
        enabled: paletteOpen && debouncedQuery.length >= 2,
    }).data;
    // ⌘K budget answers: "how much have we billed fwisd this year", "who is
    // over budget". Null when the query isn't one — search carries on below.
    const canSeeBudgets = can(PERMISSIONS.finance.view);
    const budgetAnswer = useQuery({
        queryKey: ["budget-ask", debouncedQuery],
        queryFn: () =>
            api.get<{ answer: BudgetAnswer | null }>(`/api/budgets/ask?q=${encodeURIComponent(debouncedQuery)}`),
        enabled: paletteOpen && canSeeBudgets && debouncedQuery.length >= 3,
        staleTime: 30_000,
        retry: false,
    }).data?.answer;
    const answer = budgetAnswer && query.trim() === debouncedQuery ? budgetAnswer : null;

    // ── The four counts, by name (D2 / #1203) ────────────────────────────
    // NEEDS YOU is a server number, not a row count: GET /api/tasks/counts,
    // the same computation the Tasks page heading reads. It used to be
    // `myTasks?.count ?? needsItems.length`, which counted every open task on
    // me INCLUDING ones blocked on somebody else — so the badge said 22 while
    // the page it linked to said 17.
    const work = useWorkCounts();
    const needsN = work.counts.needsYou;
    // The rows behind the door. `needsItems` is the list, never the number —
    // the number is `needsN` above.
    const needsItems = useMemo(() => (myTasks?.items ?? []).filter((t) => !t.claimable), [myTasks]);
    // SHARED INBOX. `mailN` is the whole team's open backlog (never zero,
    // demoted to a grey context line); `newMailN` is threads THIS user has never
    // opened — the clearable half.
    const mailItems = mailPeek?.threads ?? [];
    const mailN = mailPeek?.count ?? 0;
    const newMailN = mailCount?.needYou ?? 0;
    // The door carries BOTH numbers, separately labelled — it never adds them.
    // `needsN + newMailN` under the heading "Notifications" was three rule
    // breaks in one control: it summed two concepts, wore a third's name, and
    // sat beside the bell that owns that name. Amber is spent only on a real
    // exception (something breaching); otherwise the door is calm.
    const doorAttention = needsN > 0 || newMailN > 0 || breachingN > 0;
    const doorName = [
        needsN > 0 ? `${needsN} task${needsN === 1 ? "" : "s"} need${needsN === 1 ? "s" : ""} you` : null,
        newMailN > 0 ? `${newMailN} email${newMailN === 1 ? "" : "s"} need${newMailN === 1 ? "s" : ""} you` : null,
        breachingN > 0 && mailCount?.oldestWaitingLabel ? `oldest waiting ${mailCount.oldestWaitingLabel}` : null,
    ].filter(Boolean);

    // ── Unified inbox rows (email + tasks as one list; texts drop in later) ──
    const inboxRows = useMemo<UnifiedRow[]>(
        () => sortUnified([...mailItems.map(mailToRow), ...needsItems.map(taskToRow)]),
        [mailItems, needsItems],
    );
    // "On you" = mail that's new to me, plus every task on me, plus anything
    // breaching/urgent. "All" adds already-seen backlog that's still in the peek.
    const onYouRows = useMemo(() => inboxRows.filter((r) => r.newToMe || r.breaching || r.urgent), [inboxRows]);
    // Field techs never see the shared email box, so there's no On-you/All split
    // for them — just their tasks. Only office gets the filter tabs.
    const visibleRows = !isOffice ? inboxRows : inboxTab === "you" ? onYouRows : inboxRows;

    // ── Command palette model ──────────────────────────────────────────────
    // New Text / New Email are hidden until comms is built out (they routed to
    // the Mailroom composer, which is off the menu for now).
    const doActions = useMemo(
        () => [
            { label: "New Job", sub: "start a work order", run: () => quickBook.open() },
            { label: "New Task", sub: "", run: () => navigate("/tasks?new=1") },
        ],
        [quickBook, navigate],
    );
    const jumpItems = useMemo(() => {
        // #831 M9: sub-pages the launcher bands don't carry, searchable by
        // alias too — typing "stage" or "custody" must find the screen, not
        // only a customer who happens to have the word in their name.
        const JUMP_EXTRA: Array<{ label: string; to: string; keywords: string; perm?: string }> = [
            { label: "Parts Custody", to: "/inventory/parts-custody", keywords: "parts custody staging bins who has" },
            { label: "Parts to Stage", to: "/reports/parts-to-stage", keywords: "parts stage staging pull tomorrow" },
        ];
        const all = [
            ...NAV.map((n) => ({ label: n.label, to: n.to, keywords: "" })),
            ...MORE.filter((m) => (!m.adminOnly || isAdmin) && (!m.perm || can(m.perm))).map((m) => ({
                label: m.label,
                to: m.to,
                keywords: "",
            })),
            ...JUMP_EXTRA.filter((m) => !m.perm || can(m.perm)),
        ];
        const q = query.trim().toLowerCase();
        return q ? all.filter((i) => i.label.toLowerCase().includes(q) || i.keywords.includes(q)) : all.slice(0, 6);
    }, [query, isAdmin, can]);
    // Server results are already ranked best-first across all types, so we keep
    // that order flat (no re-grouping) and just show the top few.
    const findItems = query.trim().length >= 2 ? (searchResults?.results ?? []).slice(0, 8) : [];

    // Flat selectable list for arrow-key navigation.
    // Job page v2 (slice 2): on the new job page, ⌘K's first group is "This
    // job": every action from the page's one job-actions list. Typing narrows
    // it; actions that don't apply show only when you search for them, greyed
    // with the reason.
    const jobPub = useJobActionsPublication();
    type Row = {
        kind: "answer" | "job" | "do" | "jump" | "find";
        label: string;
        sub?: string;
        result?: SearchResult;
        jobAction?: JobAction;
        run: () => void;
    };
    const rows: Row[] = useMemo(() => {
        const q = query.trim().toLowerCase();
        const jobRows: Row[] = (jobPub?.actions ?? [])
            .filter((a) => (q ? matchesJobAction(a, q) : a.applies))
            .map((a) => ({
                kind: "job",
                label: a.label,
                sub: a.applies ? a.group : `${a.group} · ${a.reason ?? "not here"}`,
                jobAction: a,
                run: a.applies ? a.run : () => toast.info(`${a.label}: ${a.reason ?? "not on this job"}`),
            }));
        const doRows: Row[] = doActions
            .filter((d) => !q || d.label.toLowerCase().includes(q))
            .map((d) => ({ kind: "do", label: d.label, sub: d.sub, run: d.run }));
        const jumpRows: Row[] = jumpItems.map((j) => ({ kind: "jump", label: j.label, run: () => navigate(j.to) }));
        const findRows: Row[] = findItems.map((r) => ({
            kind: "find",
            label: r.title,
            sub: r.subtitle ? maskMoney(r.subtitle, canSeeMoney) : undefined,
            result: r,
            run: () => navigate(resultPath(r)),
        }));
        const answerRows: Row[] = !answer
            ? []
            : answer.kind === "over"
              ? [
                    ...answer.items.map<Row>((it) => ({
                        kind: "answer",
                        label: `${it.customerName} · ${it.budgetName}`,
                        sub: it.text,
                        run: () => navigate(`/customers/${it.customerId}?tab=budget`),
                    })),
                    { kind: "answer", label: "Open Money → Budgets", run: () => navigate(answer.link) },
                ]
              : [
                    {
                        kind: "answer",
                        label: `Open ${shortCustomerName(answer.customerName)} Billing & budget`,
                        run: () => navigate(answer.links.tab),
                    },
                    {
                        kind: "answer",
                        label: "Export this to Excel",
                        sub: "⌘E",
                        run: () => void downloadAuthedFile(answer.links.xlsx, "billing.xlsx").catch(() => undefined),
                    },
                ];
        return [...answerRows, ...jobRows, ...doRows, ...jumpRows, ...findRows];
    }, [answer, doActions, jumpItems, findItems, query, navigate, canSeeMoney, jobPub]);

    // Teach cards (#1838): opening ⌘K is using it (its dot goes away).
    const { markUsed } = useTeach();
    const whatsNewOn = useWhatsNewAvailable();
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const openPalette = useCallback(() => {
        setPaletteOpen(true);
        setSel(0);
        setTimeout(() => searchRef.current?.focus(), 0);
        markUsed("search");
    }, [markUsed]);
    const runRow = useCallback((r: Row) => {
        r.run();
        setPaletteOpen(false);
        setQuery("");
    }, []);

    // "More for this job" on the job page opens this palette.
    useEffect(() => {
        const onOpen = () => openPalette();
        window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    }, [openPalette]);
    // Keep the arrow-key row in view (the job list can be long).
    useEffect(() => {
        if (!paletteOpen) return;
        document.getElementById(`gwc-palette-opt-${sel}`)?.scrollIntoView?.({ block: "nearest" });
    }, [sel, paletteOpen]);

    // ⌘K / Ctrl-K opens the palette; Esc closes surfaces.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                openPalette();
            } else if (e.key === "Escape") {
                setPaletteOpen(false);
                setMoreOpen(false);
                setPanel(null);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openPalette]);

    // Outside-click closes the palette / popovers.
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (cmdRef.current && !cmdRef.current.contains(e.target as Node)) setPaletteOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const activeTab = (to: string) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

    return (
        <div className="tbv3">
            <style>{toolbarCss + bookingChipCss + fieldChipCss + quotesWaitingChipCss + deadlinesChipCss}</style>
            {/* Teach cards (#1838): What's new → Show me again. */}
            <WhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
            {/* The Quiet Toolbar's one-time card: "The bell is gone. Look for the mark on the thing." */}
            <TeachIntro featureKey="quiet.toolbar" when={quiet} />
            {/* The toolbar is the app's banner; row 2 is the primary nav. Both
                were bare <div>s, so a screen reader had no landmark to jump to. */}
            <header className="bar">
                {/* ROW 1 — command line */}
                <div className="row row1">
                    {/* OWNER-LOCKED LOGO — the official Groundwork Command wordmark
                        (LOGO_SRC). Do NOT change the logo mark, asset, or treatment
                        without Corbin's explicit, specific permission. */}
                    <Link to="/" className="logo" aria-label="Command Center">
                        <img className="wordmark" src={LOGO_SRC} alt="Groundwork Command" />
                    </Link>

                    <div
                        ref={cmdRef}
                        className={`cmd${paletteOpen ? " focus" : ""}`}
                        onClick={openPalette}
                        role="search"
                        data-teach="search"
                    >
                        <span className="ico">
                            <Search size={16} />
                        </span>
                        <input
                            ref={searchRef}
                            aria-label="Search anything or type a command"
                            role="combobox"
                            aria-expanded={paletteOpen}
                            aria-controls="gwc-palette"
                            aria-autocomplete="list"
                            aria-activedescendant={paletteOpen && rows[sel] ? `gwc-palette-opt-${sel}` : undefined}
                            placeholder={
                                jobPub
                                    ? `Search, or type what to do on job ${jobPub.jobLabel}…`
                                    : "Search anything — or type a command…"
                            }
                            value={query}
                            onFocus={openPalette}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setSel(0);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setSel((s) => Math.min(rows.length - 1, s + 1));
                                } else if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setSel((s) => Math.max(0, s - 1));
                                } else if (
                                    (e.metaKey || e.ctrlKey) &&
                                    e.key.toLowerCase() === "e" &&
                                    answer?.kind === "customer"
                                ) {
                                    e.preventDefault();
                                    void downloadAuthedFile(answer.links.xlsx, "billing.xlsx").catch(() => undefined);
                                    setPaletteOpen(false);
                                } else if (e.key === "Enter" && rows[sel]) {
                                    e.preventDefault();
                                    runRow(rows[sel]);
                                }
                            }}
                        />
                        <span className="kbd">⌘K</span>
                        <div
                            id="gwc-palette"
                            className={`palette${paletteOpen ? " open" : ""}`}
                            role="listbox"
                            aria-label="Search results and commands"
                        >
                            {rows.length === 0 && <div className="pgrp">No matches</div>}
                            {answer && (
                                <>
                                    <div className="pgrp">Answer</div>
                                    <BudgetAnswerCard answer={answer} />
                                </>
                            )}
                            {rows.map(
                                (r, i) =>
                                    r.kind === "answer" && (
                                        <PItem
                                            key={`an${i}`}
                                            id={`gwc-palette-opt-${i}`}
                                            on={i === sel}
                                            onClick={() => runRow(r)}
                                            icon={<PiggyBank size={15} />}
                                            label={r.label}
                                            sub={r.sub}
                                        />
                                    ),
                            )}
                            {jobPub && rows.some((r) => r.kind === "job") && (
                                <div className="pgrp" data-testid="palette-this-job">
                                    This job · {jobPub.jobLabel}
                                </div>
                            )}
                            {jobPub &&
                                rows.map((r, i) => {
                                    if (r.kind !== "job" || !r.jobAction) return null;
                                    const a = r.jobAction;
                                    const Icon = a.icon;
                                    const pinned = jobPub.pins.includes(a.id);
                                    return (
                                        <div
                                            key={`jb${a.id}`}
                                            id={`gwc-palette-opt-${i}`}
                                            className="pitem"
                                            style={{
                                                ...(i === sel ? { background: "var(--gold-soft)" } : {}),
                                                ...(a.applies ? {} : { color: "var(--ink-soft)" }),
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                runRow(r);
                                            }}
                                            role="option"
                                            aria-selected={i === sel}
                                            aria-disabled={!a.applies || undefined}
                                            data-testid={`palette-job-${a.id}`}
                                        >
                                            <span className="em">
                                                <Icon size={15} />
                                            </span>
                                            <span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
                                            <span className="sub" style={{ marginLeft: 0 }}>
                                                {pinned ? "pinned · " : ""}
                                                {r.sub}
                                            </span>
                                            <button
                                                type="button"
                                                aria-label={`${pinned ? "Unpin" : "Pin"} ${a.label}`}
                                                aria-pressed={pinned}
                                                title={pinned ? "Unpin from your bar" : "Pin to your bar"}
                                                data-testid={`palette-pin-${a.id}`}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    jobPub.togglePin(a.id);
                                                    markUsed("job.pins");
                                                }}
                                                style={{
                                                    background: "none",
                                                    border: 0,
                                                    padding: "0 4px",
                                                    fontSize: 16,
                                                    lineHeight: 1,
                                                    color: pinned ? "var(--gold, #b8860b)" : "var(--ink-soft)",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {pinned ? "★" : "☆"}
                                            </button>
                                        </div>
                                    );
                                })}
                            {jobPub && rows.some((r) => r.kind === "job") && (
                                <div className="pgrp" style={{ display: "flex", gap: 12, textTransform: "none" }}>
                                    <span>☆ pins it to your bar on every job</span>
                                    {!jobPub.pinsAreOfficeDefault && (
                                        <button
                                            type="button"
                                            data-testid="palette-reset-pins"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                jobPub.resetPins();
                                            }}
                                            style={{
                                                marginLeft: "auto",
                                                background: "none",
                                                border: 0,
                                                padding: 0,
                                                color: "inherit",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Reset pins to office default
                                        </button>
                                    )}
                                </div>
                            )}
                            {rows.some((r) => r.kind === "do") && <div className="pgrp">Do</div>}
                            {rows.map(
                                (r, i) =>
                                    r.kind === "do" && (
                                        <PItem
                                            key={`do${i}`}
                                            id={`gwc-palette-opt-${i}`}
                                            on={i === sel}
                                            onClick={() => runRow(r)}
                                            icon={<Plus size={15} />}
                                            label={r.label}
                                            sub={r.sub}
                                        />
                                    ),
                            )}
                            {rows.some((r) => r.kind === "jump") && <div className="pgrp">Jump to</div>}
                            {rows.map(
                                (r, i) =>
                                    r.kind === "jump" && (
                                        <PItem
                                            key={`jp${i}`}
                                            id={`gwc-palette-opt-${i}`}
                                            on={i === sel}
                                            onClick={() => runRow(r)}
                                            icon={<Target size={15} />}
                                            label={r.label}
                                        />
                                    ),
                            )}
                            {query.trim().length >= 2 && rows.some((r) => r.kind === "find") && (
                                <div className="pgrp">Find “{query.trim()}”</div>
                            )}
                            {rows.map((r, i) => {
                                if (r.kind !== "find" || !r.result) return null;
                                const meta = findMeta(r.result.type);
                                const Icon = meta.icon;
                                return (
                                    <div
                                        key={`fd${i}`}
                                        id={`gwc-palette-opt-${i}`}
                                        className="pitem"
                                        style={i === sel ? { background: "var(--gold-soft)" } : undefined}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            runRow(r);
                                        }}
                                        role="option"
                                        aria-selected={i === sel}
                                    >
                                        <span className="em" style={{ color: meta.color }}>
                                            <Icon size={15} />
                                        </span>
                                        <span className="rmid">
                                            <span className="rname">{r.label}</span>
                                            {r.sub && <span className="rctx">{r.sub}</span>}
                                        </span>
                                        <span className="rtag" style={{ background: meta.bg, color: meta.color }}>
                                            {meta.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="right">
                        {/* PRICE CHECK — the "$" backflow-cost search, sitting
                            in the global chrome right by ⌘K so "what does a test
                            cost in <city>?" is one tap from any screen. Office
                            only (pricing.view). */}
                        {can(PERMISSIONS.pricing.view) && (
                            <button
                                className="icobtn"
                                style={{ color: "var(--emerald, #059669)" }}
                                title="Price check — what a backflow test costs in a city"
                                aria-label="Price check — what a backflow test costs"
                                onClick={() => openPriceCheck()}
                            >
                                <DollarSign size={16} />
                            </button>
                        )}
                        {/* Hourly office staff punch — renders nothing for non-hourly users. */}
                        <TimeClockPunch />
                        {/* Friendly Mailroom slice 1 (10/9), behind "Try the new
                            Mailroom": ONE email number with a hover/tap preview,
                            and tasks on their own counter — never summed. */}
                        {quiet ? (
                            <>
                                {isOffice && <InboxChip />}
                                <CheckChip />
                                <TasksChip />
                                {/* Doesn't add up (trust audit Phase 3): only the
                                    viewer's own rows; gray at 0, orange 1+; never
                                    on the bell. Hidden when the switch is off. */}
                                {isOffice && <MismatchChip />}
                            </>
                        ) : isOffice && friendlyMail ? (
                            <>
                                <InboxChip />
                                {/* The ONE Tasks button, for everyone (Tasks Both
                                    Ways, pick 5A, 10/28). */}
                                <CheckChip />
                                <TasksChip />
                            </>
                        ) : (
                            <>
                                <button
                                    className="door"
                                    onClick={() => setPanel("inbox")}
                                    aria-label={
                                        doorAttention
                                            ? `Inbox — ${doorName.join(", ")}`
                                            : "Inbox — you're all caught up"
                                    }
                                >
                                    {/* Dot: green when caught up, amber when something needs you
                                — calm by default, amber only on a real difference. */}
                                    <span
                                        className="dot"
                                        style={{ background: doorAttention ? "var(--amber)" : "var(--green)" }}
                                    />{" "}
                                    Inbox
                                    {/* Email only — the task number has its own Tasks
                                button right beside this one (10/16). */}
                                    {newMailN > 0 && (
                                        <span
                                            className="n mail"
                                            title={`${newMailN} email${newMailN === 1 ? "" : "s"} need${newMailN === 1 ? "s" : ""} you${
                                                mailCount?.oldestWaitingLabel
                                                    ? ` · oldest ${mailCount.oldestWaitingLabel}`
                                                    : ""
                                            }`}
                                        >
                                            <Mail size={11} /> {newMailN}
                                        </span>
                                    )}
                                </button>
                                <CheckChip />
                                <TasksChip />
                            </>
                        )}
                        <button
                            className="door"
                            onClick={() => setPanel("help")}
                            aria-label="Help Desk"
                            data-teach="helpdesk"
                        >
                            <span className="ring">
                                <Radio size={16} />
                            </span>{" "}
                            Help Desk
                        </button>
                        {/* NOTIFICATIONS — the office half of the bell (#965).
                            The component has existed since 041 but was mounted
                            only in the tech top bar, so every createNotification()
                            written for an office user (overdue invoices, expiry,
                            stale RFQ, parts returns, task assignment, owner
                            alerts) landed in a table with nowhere to read it.
                            Sits beside the Help Desk door because that is where
                            findings are reported and where their answer arrives. */}
                        {/* Quiet toolbar (10/10): no bell. History is Recent
                            activity in the avatar menu, with no badge. */}
                        {user && !quiet && <NotificationBell triggerClassName="icobtn" />}
                        <div className="divide" />
                        <button className="icobtn" title="Theme" aria-label="Toggle theme" onClick={toggleDark}>
                            {dark ? <Moon size={16} /> : <Sun size={16} />}
                        </button>
                        <div style={{ position: "relative" }}>
                            <button
                                className="avatar"
                                onClick={() => setUserOpen((o) => !o)}
                                aria-label="Account menu"
                                aria-haspopup="true"
                                aria-expanded={userOpen}
                            >
                                {(user?.display_name ?? user?.email ?? "?").charAt(0).toUpperCase()}
                            </button>
                            {userOpen && (
                                <div
                                    className="needs-pop open"
                                    style={{ width: 240 }}
                                    role="group"
                                    aria-label="Account"
                                >
                                    {/* Were <div onClick> — not in the tab order,
                                        no role, invisible to a screen reader (#1205). */}
                                    <button type="button" className="np-i" onClick={() => navigate("/profile")}>
                                        <div className="t">Profile</div>
                                    </button>
                                    {quiet && (
                                        <button
                                            type="button"
                                            className="np-i"
                                            data-testid="recent-activity-link"
                                            onClick={() => {
                                                setUserOpen(false);
                                                navigate("/notifications");
                                            }}
                                        >
                                            <span className="em">
                                                <History size={15} />
                                            </span>
                                            <div>
                                                <div className="t">Recent activity</div>
                                                <div className="s">What happened. Nothing to clear.</div>
                                            </div>
                                        </button>
                                    )}
                                    <button type="button" className="np-i" onClick={() => navigate("/settings")}>
                                        <div className="t">Settings</div>
                                    </button>
                                    <button
                                        type="button"
                                        className="np-i"
                                        data-testid="quiet-toolbar-switch"
                                        aria-pressed={quiet}
                                        onClick={() => {
                                            setUserOpen(false);
                                            setQuiet(!quiet);
                                        }}
                                    >
                                        <div>
                                            <div className="t">
                                                {quiet ? "Back to the old toolbar" : "Try the quiet toolbar"}
                                            </div>
                                            <div className="s">
                                                {quiet
                                                    ? "Brings back the bell and extra counters"
                                                    : "No bell. Marks on the thing."}
                                            </div>
                                        </div>
                                    </button>
                                    {isOffice && (
                                        <button
                                            type="button"
                                            className="np-i"
                                            data-testid="new-requests-switch"
                                            aria-pressed={newRequests.on}
                                            onClick={() => {
                                                setUserOpen(false);
                                                newRequests.set(!newRequests.on);
                                            }}
                                        >
                                            <div>
                                                <div className="t">
                                                    {newRequests.on ? "Back to the old bookings" : "Try New requests"}
                                                </div>
                                                <div className="s">
                                                    {newRequests.on
                                                        ? "The booking chip and its list come back"
                                                        : "One list of new customers. Tap how each call went."}
                                                </div>
                                            </div>
                                        </button>
                                    )}
                                    {whatsNewOn && (
                                        <button
                                            type="button"
                                            className="np-i"
                                            onClick={() => {
                                                setUserOpen(false);
                                                setWhatsNewOpen(true);
                                            }}
                                        >
                                            <div className="t">What&apos;s new</div>
                                        </button>
                                    )}
                                    <div className="np-f">
                                        <button type="button" className="np-f-link" onClick={() => logout()}>
                                            Sign out →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ROW 2 — orientation */}
                <div className="row row2">
                    <nav className="nav" aria-label="Primary">
                        {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => {
                            // Quiet toolbar: no tab badges. The Tasks tab never has
                            // one (Tasks Both Ways, pick 5A, 10/28): the ONE Tasks
                            // button in row 1 owns the task number for everyone.
                            // Most Purchasing "needs a person" items are already
                            // generated tasks — each fact counted once.
                            const badgeN = !quiet && n.badge === "purchasing" ? purchasingN : 0;
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    title={n.tip}
                                    className={`tab${activeTab(n.to) ? " active" : ""}`}
                                >
                                    <n.icon size={15} />
                                    <span className="tl">{n.label}</span>
                                    {badgeN > 0 && <span className="tn">{badgeN}</span>}
                                </Link>
                            );
                        })}
                    </nav>
                    {/* More sits OUTSIDE the nav (10/30): the nav clips what doesn't
                        fit, and once the row's chips grew (Stuck, Deadlines,
                        bookings) it clipped More — still clickable at its sliver,
                        invisible. Now a tab name clips first; More never does. */}
                    <button
                        className="more"
                        onClick={() => setMoreOpen((o) => !o)}
                        aria-haspopup="dialog"
                        aria-expanded={moreOpen}
                    >
                        More <ChevronDown size={13} style={{ display: "inline", verticalAlign: "-2px" }} />
                    </button>

                    <div className="newpulse-wrap">
                        <div className="pulse">
                            <Stage label="New" n={pulse?.new ?? 0} />
                            <span className="sep">·</span>
                            <Stage label="Scheduled" n={pulse?.scheduled ?? 0} />
                            <span className="sep">·</span>
                            <Stage label="In field" n={pulse?.field ?? 0} />
                            <span className="sep">·</span>
                            {closeoutOnYou !== null ? (
                                <Stage label="Closeout · on you" n={closeoutOnYou} />
                            ) : (
                                <Stage label="Closeout" n={pulse?.review ?? 0} />
                            )}
                            {(pulse?.risk ?? 0) > 0 && (
                                <>
                                    <span className="sep">·</span>
                                    <span className="stage hot" title={`${pulse?.risk} job(s) past window`}>
                                        <AlertTriangle size={13} /> Stuck <b>{pulse?.risk}</b>
                                    </span>
                                </>
                            )}
                        </div>
                        {/* The amber "Needs you · N" pill used to sit here (its
                            number is the Tasks button's count now, 10/16). The
                            booking chip takes its exact spot (Book My Test, 5A):
                            office roles only, never on the bell. */}
                        {/* Add from the field: flagged tech adds only, until read.
                            Hidden at zero; never on the bell. */}
                        {/* Waiting on you (10/28): quotes waiting on the approver.
                            Approvers only; hidden at zero; never on the bell. */}
                        {/* Card won't hold (pick 2A): red, beside Closeout, opens Close
                            Out. Office only; hidden at zero; never on the bell. */}
                        {isOffice && <CloseoutHoldChip />}
                        {isAdmin && <QuotesWaitingChip />}
                        {/* Deadlines (#2096): licenses, CE, gauges, insurance due
                            within 30 days. Office + owner; hidden at zero; never
                            on the bell (expiry alerts stay off). */}
                        {isOffice && <DeadlinesChip />}
                        {isOffice && <FieldChip />}
                        {/* New requests (10/28): with "Try New requests" on, the chip
                            counts New + Trying and opens /requests; off, the
                            booking chip and its flyout, exactly as before. */}
                        {isOffice && (newRequests.on ? <RequestsChip /> : <BookingChip />)}
                    </div>
                </div>
            </header>

            {/* ── SLIDE-OVERS (over content + scrim, never a docked rail) ── */}
            <div className={`scrim${panel ? " on" : ""}`} onClick={() => setPanel(null)} />

            {/* Inbox slide-over: ONE list of what's waiting on you — email + tasks
                (texts drop in as more rows later), newest/most-urgent first. Email
                rows deep-link into the Mailroom reader on the right box; task rows
                open their owning screen. Filter: On you (clearable) vs All. */}
            <aside
                className={`panel${panel === "inbox" ? " on" : ""}`}
                aria-hidden={panel !== "inbox"}
                aria-label="Inbox"
                {...closedOverlay(panel === "inbox")}
            >
                <div className="ph">
                    <h3>Inbox</h3>
                    <span className="sub">what needs your hand</span>
                    <button type="button" className="x" onClick={() => setPanel(null)} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                <div className="pbody">
                    {/* The one amber exception: threads aged past working hours with no
                        action. Only shows when there's a genuine problem. */}
                    {isOffice && breachingN > 0 && (
                        <button
                            type="button"
                            className="thread"
                            style={{ background: "var(--amber-bg)", borderRadius: 11, margin: "2px 0 4px" }}
                            onClick={() => {
                                setPanel(null);
                                navigate("/mailroom");
                            }}
                        >
                            <div className="pic" style={{ background: "var(--amber)" }}>
                                <AlertTriangle size={16} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="l1">
                                    <span className="nm" style={{ color: "var(--amber-ink)" }}>
                                        {breachingN} email{breachingN === 1 ? "" : "s"} need you
                                    </span>
                                </div>
                                <div className="pv">
                                    Oldest waiting {mailCount?.oldestWaitingLabel} — open the Mailroom →
                                </div>
                            </div>
                        </button>
                    )}

                    {/* On you / All — same words as the Mailroom lanes. These two
                        numbers are LIST LENGTHS of the mixed list right below
                        them (tasks + email), not one of the four named counts —
                        a reader can verify them by counting rows, and the door's
                        two pills above give the per-concept split. `title` spells
                        it out so nothing has to be inferred (#1203 / D2). */}
                    {isOffice && (
                        <div className="ptabs">
                            <button
                                type="button"
                                className={`ptab${inboxTab === "you" ? " active" : ""}`}
                                onClick={() => setInboxTab("you")}
                                aria-pressed={inboxTab === "you"}
                                title={`${onYouRows.length} row${onYouRows.length === 1 ? "" : "s"} — ${needsN} task${
                                    needsN === 1 ? "" : "s"
                                } on you and ${newMailN} email${newMailN === 1 ? "" : "s"} that need you`}
                            >
                                On you {onYouRows.length > 0 && <span className="cnt">{onYouRows.length}</span>}
                            </button>
                            <button
                                type="button"
                                className={`ptab${inboxTab === "all" ? " active" : ""}`}
                                onClick={() => setInboxTab("all")}
                                aria-pressed={inboxTab === "all"}
                                title={`${inboxRows.length} row${
                                    inboxRows.length === 1 ? "" : "s"
                                } — everything in the peek, seen or not`}
                            >
                                All {inboxRows.length > 0 && <span className="cnt">{inboxRows.length}</span>}
                            </button>
                        </div>
                    )}

                    {visibleRows.length === 0 ? (
                        <div className="pempty">
                            {inboxTab === "you" || !isOffice
                                ? "Nothing waiting on you — inbox zero. Nice."
                                : "Nothing here right now."}
                        </div>
                    ) : (
                        visibleRows.map((r) => (
                            <button
                                type="button"
                                key={r.key}
                                className={`thread${r.channel === "email" && r.newToMe ? " unread" : ""}`}
                                onClick={() => {
                                    setPanel(null);
                                    // A task opens over this page; email goes to the Mailroom.
                                    if (r.channel === "task") openTaskHere(r.href, navigate);
                                    else navigate(r.href);
                                }}
                            >
                                <div
                                    className="pic"
                                    style={{
                                        background:
                                            r.channel === "email"
                                                ? mailAvatar(r.avatarSeed ?? r.title)
                                                : "var(--i-corbin)",
                                    }}
                                >
                                    {r.channel === "email" ? (
                                        mailInitial(r.avatarSeed ?? r.title)
                                    ) : (
                                        <ClipboardList size={16} />
                                    )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="l1">
                                        <span className="nm">{maskMoney(r.title, canSeeMoney)}</span>
                                        {r.breaching && (
                                            <span
                                                className="tag"
                                                style={{ background: "var(--amber-bg)", color: "var(--amber-ink)" }}
                                            >
                                                Late
                                            </span>
                                        )}
                                        {r.urgent && !r.breaching && r.channel === "email" && (
                                            <span className="tag">Customer</span>
                                        )}
                                        {r.urgent && !r.breaching && r.channel === "task" && (
                                            <span
                                                className="tag"
                                                style={{ background: "var(--amber-bg)", color: "var(--amber-ink)" }}
                                            >
                                                Urgent
                                            </span>
                                        )}
                                        <span className="tm">{peekWhen(r.when)}</span>
                                    </div>
                                    <div className="pv">{maskMoney(r.sub, canSeeMoney)}</div>
                                </div>
                            </button>
                        ))
                    )}

                    {/* Demoted team backlog — grey context, never the badge. */}
                    {isOffice && mailN > 0 && (
                        <button
                            type="button"
                            className="pmore"
                            style={{ color: "var(--ink-soft)", fontWeight: 500 }}
                            onClick={() => {
                                setPanel(null);
                                navigate("/mailroom");
                            }}
                        >
                            {mailCount
                                ? `${mailCount.team.open} open for the team${newMailN > 0 ? ` · ${newMailN} need you` : ""}`
                                : `${mailN} in the shared box`}{" "}
                            — open the Mailroom →
                        </button>
                    )}

                    {/* Jump links. */}
                    <div className="psec" style={{ marginTop: 4 }}>
                        {/* Were <a> with no href — no link role, not focusable. */}
                        <button
                            type="button"
                            className="go"
                            style={{ marginLeft: 0 }}
                            onClick={() => {
                                setPanel(null);
                                navigate("/tasks");
                            }}
                        >
                            Open Tasks →
                        </button>
                        {isOffice && (
                            <button
                                type="button"
                                className="go"
                                onClick={() => {
                                    setPanel(null);
                                    navigate("/mailroom");
                                }}
                            >
                                Open Mailroom →
                            </button>
                        )}
                    </div>
                </div>
            </aside>

            <aside
                className={`panel${panel === "help" ? " on" : ""}`}
                aria-hidden={panel !== "help"}
                aria-label="Help Desk"
                {...closedOverlay(panel === "help")}
            >
                <div className="ph">
                    <h3>
                        <Radio size={16} style={{ display: "inline", verticalAlign: "-2px", marginRight: 6 }} />
                        Help Desk
                    </h3>
                    {!(chat.active && helpTab === "ask") && <span className="sub">answers + does it for you</span>}
                    {chat.active && helpTab === "ask" && (
                        <button type="button" className="hck-link hnew" onClick={chat.reset}>
                            New question
                        </button>
                    )}
                    <button type="button" className="x" onClick={() => setPanel(null)} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                {canFlag && (
                    <div className="htabs" role="tablist" aria-label="Help Desk">
                        <button
                            type="button"
                            role="tab"
                            className="htab"
                            aria-selected={helpTab === "ask"}
                            onClick={() => setHelpTab("ask")}
                        >
                            Ask
                        </button>
                        <button
                            type="button"
                            role="tab"
                            className="htab"
                            aria-selected={helpTab === "mine"}
                            onClick={() => setHelpTab("mine")}
                        >
                            My questions
                            {toCheck > 0 && (
                                <span className="n" aria-label={`${toCheck} to check`}>
                                    {toCheck}
                                </span>
                            )}
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                role="tab"
                                className="htab"
                                aria-selected={helpTab === "review"}
                                onClick={() => setHelpTab("review")}
                            >
                                Review
                                {reviewN > 0 && (
                                    <span className="n" aria-label={`${reviewN} waiting for your OK`}>
                                        {reviewN}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                )}
                {chat.active && helpTab === "ask" ? (
                    <SupportCopilotThread chat={chat} />
                ) : (
                    <>
                        <div className="hbody">
                            {helpTab === "review" && isAdmin && panel === "help" ? (
                                <HelpDeskReview />
                            ) : helpTab === "mine" && canFlag && panel === "help" ? (
                                <MyQuestions />
                            ) : helpJobId && panel === "help" ? (
                                <JobCheckupPanel jobId={helpJobId} />
                            ) : (
                                <div className="hb gw">
                                    {/* #1703 — only what's true. "The shop's calm… I've got the
                                rest" sat over a page showing Stuck 2 (2026-10-01). */}
                                    Hi {user?.display_name?.split(" ")[0] ?? "there"} — what can I help with?
                                    {!work.isError && needsN > 0 && (
                                        <>
                                            {" "}
                                            {needsN} thing{needsN === 1 ? " needs" : "s need"} you today.
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {(helpTab === "mine" && canFlag) || (helpTab === "review" && isAdmin) ? null : helpJobId ? (
                            <div className="hchips">
                                <button
                                    type="button"
                                    className="hchip"
                                    onClick={() => askHelpDesk("Something's wrong")}
                                >
                                    Something else is wrong
                                </button>
                            </div>
                        ) : (
                            <div className="hchips">
                                <button
                                    type="button"
                                    className="hchip"
                                    onClick={() => {
                                        setPanel(null);
                                        navigate("/tasks");
                                    }}
                                >
                                    What needs me today?
                                </button>
                                <button type="button" className="hchip" onClick={() => askHelpDesk("How do I…?")}>
                                    How do I…?
                                </button>
                                <button
                                    type="button"
                                    className="hchip"
                                    onClick={() => askHelpDesk("Something's wrong")}
                                >
                                    Something&apos;s wrong
                                </button>
                            </div>
                        )}
                        <div
                            className="hask"
                            hidden={(helpTab === "mine" && canFlag) || (helpTab === "review" && isAdmin)}
                        >
                            <input
                                aria-label="Ask the Help Desk"
                                placeholder={
                                    helpJobId ? "Something else wrong? Tell me…" : "Ask anything, or tell me to do it…"
                                }
                                value={helpText}
                                onChange={(e) => setHelpText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") askHelpDesk();
                                }}
                            />
                            <button
                                type="button"
                                className="go"
                                aria-label="Send to the Help Desk"
                                onClick={() => askHelpDesk()}
                            >
                                <Send size={15} />
                            </button>
                        </div>
                    </>
                )}
            </aside>

            {/* ── "More" launcher (mockups/gwc-more-section.html) ───────────── */}
            <div className={`scrim${moreOpen ? " on" : ""}`} onClick={() => setMoreOpen(false)} />
            <div
                className={`lchr${moreOpen ? " on" : ""}`}
                role="dialog"
                aria-label="More — destinations and settings"
                aria-hidden={!moreOpen}
                {...closedOverlay(moreOpen)}
            >
                <div className="lc-head">
                    <button
                        type="button"
                        className="lc-search"
                        onClick={() => {
                            setMoreOpen(false);
                            openPalette();
                        }}
                    >
                        <Search size={16} />
                        <span>Search anything — customer, address, serial #, invoice, or an action</span>
                    </button>
                    <button
                        className="lc-door"
                        onClick={() => {
                            setMoreOpen(false);
                            navigate("/settings");
                        }}
                    >
                        <Settings size={15} /> Settings
                    </button>
                </div>
                <div className="lc-body">
                    {(
                        [
                            ["Start here", "— where you begin work", LAUNCH_START],
                            ["Queues", "— clear these to zero", launchQueueFor(LAUNCH_QUEUE, newRequests.on)],
                            ["Run the business", "— weekly, not daily", launchRunFor(LAUNCH_RUN, newRequests.on)],
                            ...(LAUNCH_PEOPLE.some((t) => !t.perm || can(t.perm))
                                ? [["People", "— who's in, who's out", LAUNCH_PEOPLE]]
                                : []),
                            // Admin group only renders for admins (all tiles are
                            // adminOnly); hide the whole section otherwise (#200).
                            ...(isAdmin ? [["Admin", "— setup & configuration", LAUNCH_ADMIN]] : []),
                        ] as Array<[string, string, LaunchTile[]]>
                    ).map(([title, hint, tiles]) => (
                        <div className="lc-sec" key={title}>
                            <h4>
                                {title} <small>{hint}</small>
                            </h4>
                            <div className="tiles">
                                {tiles
                                    .filter((t) => (!t.adminOnly || isAdmin) && (!t.perm || can(t.perm)))
                                    .map((t) => {
                                        // Quiet toolbar: no launcher Closeout badge — it
                                        // is the same number as "Closeout" in the strip.
                                        const badgeN = !quiet && t.badge === "review" ? (pulse?.review ?? 0) : 0;
                                        return (
                                            <button
                                                key={t.to}
                                                className="tile"
                                                onClick={() => {
                                                    setMoreOpen(false);
                                                    navigate(t.to);
                                                }}
                                            >
                                                <span className="ti">
                                                    <t.icon size={19} />
                                                </span>
                                                <span className="tt">{t.label}</span>
                                                <span className="ts">{t.sub}</span>
                                                {badgeN > 0 && <span className="tb">{badgeN}</span>}
                                            </button>
                                        );
                                    })}
                            </div>
                        </div>
                    ))}
                    <div className="moved">
                        <b>Moved, not lost.</b> These stopped being their own menu items — you reach them faster now
                        (routes stay live; they just leave the menu):
                        <div>
                            {MOVED.map(([name, where]) => (
                                <span className="m" key={name}>
                                    {name} → <em>{where}</em>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PItem({
    id,
    on,
    onClick,
    icon,
    label,
    sub,
}: {
    id: string;
    on: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    sub?: string;
}) {
    return (
        <div
            id={id}
            className="pitem"
            style={on ? { background: "var(--gold-soft)" } : undefined}
            onMouseDown={(e) => {
                e.preventDefault();
                onClick();
            }}
            role="option"
            aria-selected={on}
        >
            <span className="em">{icon}</span> {label}
            {sub ? <span className="sub">{sub}</span> : null}
        </div>
    );
}

function Stage({ label, n }: { label: string; n: number }) {
    return (
        <span className="stage">
            {label} <b>{n}</b>
        </span>
    );
}
